import fp from "lodash/fp"

import type { EntityType } from "types/common.types"
import type { CsvDownloadGroup } from "types/csv.types"
import type { QueryPlotDef } from "types/dashboard.types"
import type { Device, IDeviceWRelsById } from "types/device.types"
import type { Geolocation } from "types/geolocation.types"
import type { Tower } from "types/towers.types"

import {
  deviceTabPlotDefs,
  getTowerTabPlotDefs,
  powerlineInfoTabPlotDefs,
  positionInPowerlinePlotDef,
  spanOverviewTabPlotDefs,
  getSpanTabPlotDefs,
  getTowerInfoTabPlotDefs,
  towerTabPlotDefs,
  getTowerDevicesTabPlotDefs,
  getTowerAmbientTabPlotDefs,
  getTowerSegmentsTabPlotDefs,
} from "helpers/config/dashboards"
import {
  AMPACITY_AAR_CRITICAL_SPAN_FIELD,
  AMPACITY_DLR_CRITICAL_SPAN_FIELD,
  MOTION_JERK_NORM_FIELD,
} from "helpers/config/telemetryFieldDefs"
import { getDeviceAmbientConfig } from "helpers/utils/deviceAmbient"
import { isTowerHealthEnabled, getTowerSegments } from "helpers/utils/towerHealth"
import { resolveEntityIds } from "helpers/utils/crossEntityPlots"
import { getGeohash } from "helpers/utils/common"

// Always use dual-model sag def for CSV — includes all fields
const allSpanTabPlotDefs = getSpanTabPlotDefs({
  sag: { config: "inclination_and_thermal_model", enabled: true, was_enabled: true },
})

// The tower CSV mirrors the layout that is actually rendered, which depends on the
// tower_health feature flag. Only downloadable plots are included.
// Segment defs are dynamic (fields depend on the tower's configured segments), so they
// are built per-tower from getTowerSegments(tower.settings).
const getTowerCsvPlotDefs = (tower: Tower | undefined): QueryPlotDef[] => {
  const layoutDefs =
    tower && isTowerHealthEnabled(tower)
      ? [
          ...towerTabPlotDefs, // map + global lean
          ...getTowerSegmentsTabPlotDefs(getTowerSegments(tower.settings)), // per-segment telemetries
          ...getTowerDevicesTabPlotDefs(),
          ...getTowerAmbientTabPlotDefs(),
        ]
      : getTowerInfoTabPlotDefs() // legacy
  return layoutDefs.filter((def) => def.downloadable !== false)
}

export const getDefsForEntity = (
  entity: EntityType,
  tower: Tower | undefined,
  device: Device | undefined,
): QueryPlotDef[] => {
  switch (entity) {
    case "powerline":
      return powerlineInfoTabPlotDefs
    case "span":
      return spanOverviewTabPlotDefs
    case "tower":
      return getTowerCsvPlotDefs(tower)
    case "device":
    default:
      return [
        positionInPowerlinePlotDef,
        ...allSpanTabPlotDefs,
        ...deviceTabPlotDefs,
        ...getTowerTabPlotDefs({}),
        ...(getDeviceAmbientConfig(device)?.plotDefs ?? []),
      ]
  }
}

export const defsNeedDevices = (defs: QueryPlotDef[]): boolean =>
  defs.some(
    (def) =>
      def.dataSource?.service === "telemetry" && def.dataSource.resolver !== "self",
  )

export const getLocationGeohash = (
  location: Geolocation | null | undefined,
): string | undefined =>
  location?.latitude && location?.longitude
    ? getGeohash(location.latitude, location.longitude)
    : undefined

const CSV_EXCLUDED_FIELDS = new Set([MOTION_JERK_NORM_FIELD])

interface CsvExtraField {
  entity: EntityType
  resolver: "self"
  service: "telemetry"
  measurement?: string
  fields: string[]
}

const CSV_EXTRA_FIELDS: Partial<Record<EntityType, CsvExtraField[]>> = {
  powerline: [
    {
      entity: "powerline",
      resolver: "self",
      service: "telemetry",
      measurement: "dlr",
      fields: [AMPACITY_AAR_CRITICAL_SPAN_FIELD, AMPACITY_DLR_CRITICAL_SPAN_FIELD],
    },
  ],
}

export type GroupsByKey = Record<string, CsvDownloadGroup>

const getGroupKey = (prefix: string, measurement: string | undefined): string =>
  measurement ? `${prefix}__${measurement}` : prefix

const mergeFields = (
  currentFields: string[] | undefined,
  newFields: string[],
): string[] | undefined => {
  if (newFields.length === 0) return currentFields
  return fp.uniq([...(currentFields ?? []), ...newFields]) as string[]
}

/** Adds `group` under `key`, folding its ids and fields into an existing one. */
const upsertGroup = (
  acc: GroupsByKey,
  key: string,
  group: CsvDownloadGroup,
): GroupsByKey => {
  const existing = acc[key]
  if (!existing) return { ...acc, [key]: group }
  return {
    ...acc,
    [key]: {
      ...existing,
      ids: fp.uniq([...existing.ids, ...group.ids]) as number[],
      fields: mergeFields(existing.fields, group.fields ?? []),
    },
  }
}

export interface BuildGroupsContext {
  entityId: number
  contextEntity: Device | undefined
  devsWRelsById: IDeviceWRelsById | undefined
  towerGeohash: string | undefined
  deviceGeohash: string | undefined
}

export const addWeatherGroup = (
  acc: GroupsByKey,
  def: QueryPlotDef,
  { towerGeohash, deviceGeohash }: BuildGroupsContext,
): GroupsByKey => {
  if (def.dataSource?.service !== "weather") return acc
  const geohash = def.dataSource.entity === "device" ? deviceGeohash : towerGeohash
  if (!geohash) return acc

  const measurement =
    "measurement" in def.reqConfig ? def.reqConfig.measurement : undefined
  const fields = def.reqConfig?.fields ?? []

  return upsertGroup(acc, getGroupKey("weather", measurement), {
    entity: def.dataSource.entity,
    ids: [],
    measurement,
    fields: fields.length > 0 ? [...fields] : undefined,
    geohash,
  })
}

export const addTelemetryGroup = (
  acc: GroupsByKey,
  def: QueryPlotDef,
  { entityId, contextEntity, devsWRelsById }: BuildGroupsContext,
): GroupsByKey => {
  if (def.dataSource?.service !== "telemetry") return acc

  // Some resolvers (like "device.span") read from contextEntity.
  // Skip until devices have loaded to avoid crashes.
  const resolverNeedsContext =
    def.dataSource.resolver !== "self" &&
    !["tower.devices"].includes(def.dataSource.resolver)
  if (resolverNeedsContext && !contextEntity) return acc

  const ids = resolveEntityIds(def.dataSource, entityId, contextEntity, devsWRelsById)
  if (ids.length === 0) return acc

  const targetEntity = def.dataSource.entity
  const measurement =
    "measurement" in def.reqConfig ? def.reqConfig.measurement : undefined
  const fields = (def.reqConfig?.fields ?? []).filter((f) => !CSV_EXCLUDED_FIELDS.has(f))

  return upsertGroup(acc, getGroupKey(targetEntity, measurement), {
    entity: targetEntity,
    ids: [...ids],
    measurement,
    fields: fields.length > 0 ? [...fields] : undefined,
  })
}

export const applyExtraFields = (
  acc: GroupsByKey,
  entity: EntityType,
  entityId: number,
): GroupsByKey =>
  (CSV_EXTRA_FIELDS[entity] ?? []).reduce(
    (groups, extra) =>
      upsertGroup(groups, getGroupKey(extra.entity, extra.measurement), {
        entity: extra.entity,
        ids: [entityId],
        measurement: extra.measurement,
        fields: [...extra.fields],
      }),
    acc,
  )
